import * as React from 'react';
import { Container, Grid, Typography, Paper, IconButton } from '@material-ui/core';
import { graphql, useStaticQuery } from 'gatsby';
import FilterListIcon from '@material-ui/icons/FilterList';
import { Languages } from '../utils/getAllSupportedLanguages';
import FilterSortDialogMobile from '../components/FilterSortDialogMobile';
import SearchResultTable from '../components/SearchResultTable';
import Seo from '../components/Seo';
import FilterSortFields from '../components/FilterSortFields';
import { FilterSort, LocationLanguageContextType, BrowseQueryProps } from '../typings';
import { filterTemplates } from '../utils/filterTemplates';
import { sortTemplates } from '../utils/sortTemplates';
import {
  getTemplateCategoryByLocale,
  getCategoriesWithTemplates,
  setLanguageFilterByGeo,
  getDefaultFilters
} from '../utils/getFilterOptions';
import { LocationLanguageContext } from '../contexts/LocationLanguageContext';
import { matchesMobile } from '../utils/mediaQuery';

const strapiBrowseLayoutQuery = graphql`
  query BrowseLayoutQuery {
    allStrapiBrowsePage {
      nodes {
        Title
        locale
      }
    }
    allStrapiTemplate(sort: { fields: Title }) {
      nodes {
        id
        published_at
        Title
        Description
        SeoContent
        Keywords
        Author
        Slug
        categories {
          name
        }
        trades {
          name
        }
        template_language {
          name
        }
      }
    }
    allStrapiCategories(sort: { fields: rank, order: ASC }) {
      nodes {
        name
        rank
        Localizations {
          displayName
          locale
          localeName
        }
        templates {
          id
        }
      }
    }
    allStrapiTrades {
      nodes {
        name
        Localizations {
          displayName
          locale
          localeName
        }
        templates {
          id
        }
      }
    }
    allStrapiTemplateLanguage {
      nodes {
        Localizations {
          displayName
          locale
          localeName
        }
        name
        templates {
          id
        }
      }
    }
  }
`;

const BrowseLayout: React.FC<BrowseQueryProps> = ({ filterQuery, locationState }) => {
  // Support direct URL param ?filters=category:Progress when Gatsby page context doesn't supply filterQuery prop.
  let effectiveFilterQuery = filterQuery;
  if (typeof window !== 'undefined' && !effectiveFilterQuery) {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('filters');
    if (raw) {
      effectiveFilterQuery = raw; // expected format category:Progress or trade:Something
    }
  }
  const data = useStaticQuery(strapiBrowseLayoutQuery);

  const locationLanguage = React.useContext<LocationLanguageContextType | null>(LocationLanguageContext);
  const browserLocale: string = locationLanguage?.language ?? 'en'; // e.g. 'en', 'fr'

  const templates = data.allStrapiTemplate.nodes;
  const [templateResult, setTemplateResult] = React.useState(templates);
  const [filterSortValue, setFilterSortValue] = React.useState<FilterSort>(() => getDefaultFilters([Languages.English]));
  const [isOpenFilterSortDialog, setIsOpenFilterSortDialog] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const language: string = browserLocale;

  const browsePageContent = data.allStrapiBrowsePage.nodes.find((x: any) => x.locale === language);

  // Redirect back to the browse page when 'browse' is clicked
  React.useEffect(() => {
    if (locationState) {
      // Reset filters to defaults using current language filter selection
      setFilterSortValue((prev) => getDefaultFilters(prev.filters.language));
    }
  }, [locationState]);

  const categories = getTemplateCategoryByLocale(getCategoriesWithTemplates(data.allStrapiCategories.nodes), language);
  const trades = getTemplateCategoryByLocale(getCategoriesWithTemplates(data.allStrapiTrades.nodes), language);
  const allTemplateLanguages = getTemplateCategoryByLocale(getCategoriesWithTemplates(data.allStrapiTemplateLanguage.nodes), language);

  /**
   * Initialize language filter based on browser language when component mounts
   * or when template languages are loaded.
   */
  React.useEffect(() => {
    if (!isInitialized && allTemplateLanguages) {
      const derivedLangFilter = setLanguageFilterByGeo(browserLocale, allTemplateLanguages);

      // Build initial filters with derived language filter
      const buildInitialFilters = (): FilterSort => {
        if (!effectiveFilterQuery) {
          return getDefaultFilters(derivedLangFilter);
        }
        const [type, value] = effectiveFilterQuery.split(':');
        const isCategoryQuery = type === 'category';
        const categoryValue = isCategoryQuery ? [value] : [];
        return {
          filters: {
            language: derivedLangFilter,
            categories: categoryValue,
            trades: []
          },
          sort: 'titleasc'
        };
      };

      setFilterSortValue(buildInitialFilters());
      setIsInitialized(true);
    }
  }, [isInitialized, allTemplateLanguages, browserLocale, effectiveFilterQuery]);

  /**
   * Filter and sort templates whenever filters or sort options change
   */
  React.useEffect(() => {
    let tempResult = filterTemplates(templates, filterSortValue.filters);
    tempResult = sortTemplates(tempResult, filterSortValue.sort);
    setTemplateResult([...tempResult]);
    return () => {
      setTemplateResult([]);
    };
  }, [filterSortValue.filters, filterSortValue.sort, templates]);

  const openFilterSortDialog = () => {
    setIsOpenFilterSortDialog(true);
  };

  const handleFilterSortDialogClose = (value: any) => {
    if (value) {
      setFilterSortValue(value);
    }
    setIsOpenFilterSortDialog(false);
  };

  /**
   * Handle filter/sort changes from user interactions.
   * This allows manual language filter selection to override the initial browser-based selection.
   */
  const handleFilterSortChange = (filteredValue: FilterSort) => {
    setFilterSortValue(filteredValue);
  };
  const isMobile = matchesMobile();
  if (browsePageContent) {
    return (
      <>
        <Seo title='Browse' isTemplatePage={false} />
        <Container maxWidth='xl' style={{ marginTop: '1.5rem' }}>
          <Grid container spacing={3}>
            {isMobile ? null : (
              <Grid item xs={12} sm={3}>
                <Paper style={{ padding: '1rem 1rem' }}>
                  <FilterSortFields
                    filterSortObj={filterSortValue}
                    onFilterSortChange={handleFilterSortChange}
                    isBrowsePage
                    languages={allTemplateLanguages}
                    categories={categories}
                    trades={trades}
                    locationState={locationState}
                  />
                </Paper>
              </Grid>
            )}
            <Grid item xs={12} sm={9}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography style={{ marginBottom: '0', fontSize: '1.45rem', fontWeight: 400 }} variant='h1'>
                  {browsePageContent.Title}
                </Typography>
                {isMobile ? (
                  <IconButton aria-label='search' onClick={openFilterSortDialog}>
                    <FilterListIcon />
                  </IconButton>
                ) : null}
              </div>
              {templateResult ? (
                <div>
                  <SearchResultTable searchResults={templateResult} isMobile={isMobile} />
                </div>
              ) : null}
              <FilterSortDialogMobile
                filterSortObj={filterSortValue}
                isBrowsePage
                open={isOpenFilterSortDialog}
                onDialogClose={handleFilterSortDialogClose}
                languages={allTemplateLanguages}
                categories={categories}
                trades={trades}
                locationState={locationState}
              />
            </Grid>
          </Grid>
        </Container>
      </>
    );
  }

  return null;
};
export default BrowseLayout;
